import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Tabs } from 'antd';
import { FM140, FM141 } from '../../services/outboundNoticeApi';
import { useOutboundNoticeForm, DOC_TYPE_LABELS, VOUCHER_TYPE_LABELS } from './hooks/useOutboundNoticeForm';
import LookupField from '../../components/LookupField';
import './M206Form.css';

/** Helper: convert ISO date string to input value */
const dateToInput = (v: FM140[keyof FM140]): string => {
  if (!v) return '';
  const s = String(v);
  return s.length >= 10 ? s.substring(0, 10) : s;
};

const M206Form: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('1');
  const {
    mode, setMode, isEditing,
    record, formData, details, loading,
    allIds, currentIdx, setCurrentIdx,
    setDetails,
    loadList, loadRecord, navTo,
    setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    addDetailRow, deleteDetailRow,
  } = useOutboundNoticeForm();

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecord(ids[0]);
        }
      } catch {
        console.error('Failed to load M206 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mode Actions
  const handleAddAction = useCallback(async () => {
    if (isEditing && mode === 'add') {
      await handleSave(mode, async (result: FM140) => {
        const ids = await loadList();
        const newIdx = ids.indexOf(result.fm140_01);
        setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      setActiveTab('1');
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd]);

  const handleModifyAction = useCallback(async () => {
    if (isEditing && mode === 'edit') {
      await handleSave(mode, async (result: FM140) => {
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('edit');
    }
  }, [isEditing, mode, handleSave, applyResult, setMode]);

  const handleDeleteAction = useCallback(() => {
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除出庫通知單 ${record?.fm140_01} 嗎?`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        await handleDelete(async () => {
          const ids = await loadList();
          if (ids.length > 0) {
            setCurrentIdx(0);
            await loadRecord(ids[0]);
          } else {
            resetForAdd();
          }
          setMode('browse');
        });
      },
    });
  }, [record, handleDelete, loadList, setCurrentIdx, loadRecord, resetForAdd, setMode]);

  const handleExit = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      setMode('browse');
    } else {
      navigate('/erp');
    }
  }, [isEditing, revertToRecord, setMode, navigate]);

  const handleDetailChange = useCallback((idx: number, key: keyof FM141, value: FM141[keyof FM141]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], [key]: value };
      }
      return next;
    });
  }, [setDetails]);

  const docType = (formData.fm140_12 as string) || '';
  const isTypeD = docType === 'D';
  const isDisabled = !isEditing;

  if (loading && !record && formData.fm140_01 === undefined) {
    return <div style={{ padding: 24, textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div className="m206-form">
      <div className="page-wrapper">
        {/* Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <div className="breadcrumb-inline">
              <a href="/erp">ERP</a><span>/</span>
              <span className="current">出庫通知單</span>
            </div>
            <span className="title-separator">|</span>
            <h1>出庫通知單 (pwm206f1)</h1>
          </div>
          <div className="page-title-right">
            <span style={{ fontSize: 12, color: '#999' }}>
              {record?.fm140_01 || '(新增)'}
            </span>
          </div>
        </div>

        {/* Info Bar */}
        <div className="form-info-bar">
          <div className="field-group">
            <label>單號</label>
            <strong>{formData.fm140_01 || '--'}</strong>
          </div>
          <div className="field-group">
            <label>模式</label>
            <span className={`badge ${isEditing ? 'badge-warning' : 'badge-default'}`}>
              {mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽'}
            </span>
          </div>
          <div className="field-group">
            <label>類別</label>
            <span>{DOC_TYPE_LABELS[docType] || docType || '--'}</span>
          </div>
          <div className="field-group">
            <label>明細</label>
            <span>{details.length}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="form-content">
          <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
            {
              key: '1', label: '基本資料',
              children: (
                <div className="m206-tab1">
                  <div className="form-section">
                    <span className="section-tag">單頭資訊</span>
                    <div className="form-grid-4col">
                      <label>出庫單號</label>
                      <input type="text" readOnly value={getField('fm140_01') as string} />

                      <label className="required">填單日期</label>
                      <input type="date" disabled={isDisabled}
                        value={dateToInput(getField('fm140_11'))}
                        onChange={e => setField('fm140_11', e.target.value || null)} />

                      <label className="required">單據類別</label>
                      <select disabled={isDisabled}
                        value={getField('fm140_12') as string}
                        onChange={e => setField('fm140_12', e.target.value)}>
                        <option value="A">A - P/O退貨</option>
                        <option value="C">C - 庫位移轉</option>
                        <option value="D">D - 客訴買收交運單</option>
                        <option value="E">E - 客戶寄庫交運單</option>
                      </select>

                      <label className="required">收貨廠商</label>
                      <LookupField lookupType="vendor" maxLength={12}
                        disabled={isDisabled}
                        value={(getField('fm140_13') as string) || ''}
                        onChange={v => setField('fm140_13', v)}
                        onCodeConfirm={v => setField('fm140_13', v)}
                        style={{ width: '100%' }} />

                      <label className="required">出庫庫位</label>
                      <LookupField lookupType="warehouse" maxLength={5}
                        disabled={isDisabled}
                        value={(getField('fm140_16') as string) || ''}
                        onChange={v => setField('fm140_16', v)}
                        onCodeConfirm={v => setField('fm140_16', v)}
                        style={{ width: '100%' }} />

                      <label>入庫庫位</label>
                      <LookupField lookupType="warehouse" maxLength={5}
                        disabled={isDisabled || isTypeD}
                        value={(getField('fm140_20') as string) || ''}
                        onChange={v => setField('fm140_20', v)}
                        onCodeConfirm={v => setField('fm140_20', v)}
                        style={{ width: '100%' }} />

                      <label>希望出庫日</label>
                      <input type="date" disabled={isDisabled}
                        value={dateToInput(getField('fm140_17'))}
                        onChange={e => setField('fm140_17', e.target.value || null)} />

                      <label>使用訂單號碼</label>
                      <input type="text" maxLength={7} disabled={isDisabled}
                        value={getField('fm140_18') as string}
                        onChange={e => setField('fm140_18', e.target.value)} />

                      <label>回應單據號碼</label>
                      <input type="text" readOnly
                        value={getField('fm140_15') as string} />

                      <label>填單人</label>
                      <input type="text" maxLength={10} disabled={isDisabled}
                        value={getField('fm140_91') as string}
                        onChange={e => setField('fm140_91', e.target.value)} />
                    </div>
                  </div>

                  {/* D-type fields */}
                  <div className="form-section">
                    <span className="section-tag">客訴/幣別 (D類型專用)</span>
                    <div className="form-grid-4col">
                      <label>幣別</label>
                      <LookupField lookupType="currency" maxLength={3}
                        disabled={isDisabled || !isTypeD}
                        value={(getField('fm140_22') as string) || ''}
                        onChange={v => setField('fm140_22', v)}
                        onCodeConfirm={v => setField('fm140_22', v)}
                        style={{ width: '100%' }} />

                      <label>憑證類別</label>
                      <select disabled={isDisabled || !isTypeD}
                        value={getField('fm140_21') as number}
                        onChange={e => setField('fm140_21', Number(e.target.value))}>
                        {Object.entries(VOUCHER_TYPE_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>

                      <label>客訴單號</label>
                      <LookupField lookupType="complaintOrder" value={(getField('fm140_23') as string) || ''}
                        onChange={(code) => setField('fm140_23', code)}
                        disabled={isDisabled || !isTypeD} style={{ width: '100%' }} title="客訴單號搜尋" />

                      <label>計稅</label>
                      <input type="checkbox" disabled={isDisabled || !isTypeD}
                        checked={!!getField('fm140_24')}
                        onChange={e => setField('fm140_24', e.target.checked)} />
                    </div>
                  </div>

                  <div className="form-section">
                    <span className="section-tag">彙總</span>
                    <div className="form-grid-4col">
                      <label>出庫總數量</label>
                      <input type="text" readOnly
                        value={String(formData.fm140_14 ?? 0)} />
                      <label>總項數</label>
                      <input type="text" readOnly
                        value={String(formData.fm140_19 ?? details.length)} />
                    </div>
                  </div>
                </div>
              ),
            },
            {
              key: '2', label: `明細 (${details.length})`,
              children: (
                <div className="m206-tab2">
                  {isEditing && (
                    <div style={{ marginBottom: 8 }}>
                      <button className="btn btn-primary btn-sm" onClick={addDetailRow}>
                        + 新增明細
                      </button>
                    </div>
                  )}
                  <table className="detail-table">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}>#</th>
                        <th style={{ width: 80 }}>產品代號1</th>
                        <th style={{ width: 80 }}>產品代號2</th>
                        <th style={{ width: 120 }}>批號</th>
                        <th style={{ width: 60 }}>等級</th>
                        <th style={{ width: 60 }}>出庫單位</th>
                        <th style={{ width: 80 }}>出庫數量</th>
                        <th style={{ width: 70 }}>單價</th>
                        <th style={{ width: 80 }}>金額</th>
                        <th style={{ width: 60 }}>換算率</th>
                        {isTypeD && <th style={{ width: 70 }}>D單價</th>}
                        {isEditing && <th style={{ width: 40 }}>刪</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {details.length === 0 && (
                        <tr>
                          <td colSpan={isEditing ? 12 : 10}
                            style={{ textAlign: 'center', color: '#999', padding: 16 }}>
                            尚無明細資料
                          </td>
                        </tr>
                      )}
                      {details.map((d, idx) => (
                        <tr key={idx}>
                          <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                          <td><input type="text" maxLength={6} disabled={isDisabled}
                            value={d.fm141_12 || ''} style={{ width: '100%' }}
                            onChange={e => handleDetailChange(idx, 'fm141_12', e.target.value)} /></td>
                          <td><input type="text" maxLength={6} disabled={isDisabled}
                            value={d.fm141_121 || ''} style={{ width: '100%' }}
                            onChange={e => handleDetailChange(idx, 'fm141_121', e.target.value)} /></td>
                          <td><input type="text" maxLength={16} disabled={isDisabled}
                            value={d.fm141_11 || ''} style={{ width: '100%' }}
                            onChange={e => handleDetailChange(idx, 'fm141_11', e.target.value)} /></td>
                          <td><input type="text" maxLength={5} disabled={isDisabled}
                            value={d.fm141_13 || ''} style={{ width: '100%' }}
                            onChange={e => handleDetailChange(idx, 'fm141_13', e.target.value)} /></td>
                          <td><input type="text" maxLength={5} disabled={isDisabled || isTypeD}
                            value={d.fm141_14 || ''} style={{ width: '100%' }}
                            onChange={e => handleDetailChange(idx, 'fm141_14', e.target.value)} /></td>
                          <td><input type="number" step="0.01" disabled={isDisabled}
                            value={d.fm141_16 ?? 0} style={{ width: '100%' }}
                            onChange={e => handleDetailChange(idx, 'fm141_16', Number(e.target.value))} /></td>
                          <td><input type="number" step="0.01" readOnly
                            value={d.fm141_15 ?? 0} style={{ width: '100%' }} /></td>
                          <td><input type="number" step="0.01" readOnly
                            value={d.fm141_17 ?? 0} style={{ width: '100%' }} /></td>
                          <td><input type="number" step="0.0001" disabled={isDisabled || isTypeD}
                            value={d.fm141_20 ?? 1} style={{ width: '100%' }}
                            onChange={e => handleDetailChange(idx, 'fm141_20', Number(e.target.value))} /></td>
                          {isTypeD && (
                            <td><input type="number" step="0.01" disabled={isDisabled}
                              value={d.fm141_23 ?? 0} style={{ width: '100%' }}
                              onChange={e => handleDetailChange(idx, 'fm141_23', Number(e.target.value))} /></td>
                          )}
                          {isEditing && (
                            <td style={{ textAlign: 'center' }}>
                              <button className="btn btn-danger btn-sm"
                                onClick={() => deleteDetailRow(idx)} title="刪除">X</button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ),
            },
          ]} />
        </div>

        {/* Action Bar */}
        <div className="action-bar">
          <div className="action-bar-left">
            <button className="btn btn-primary" onClick={handleAddAction}>
              {isEditing && mode === 'add' ? '儲存' : '新增'}
            </button>
            <button className="btn" disabled={!record} onClick={handleModifyAction}>
              {isEditing && mode === 'edit' ? '儲存' : '修改'}
            </button>
            <button className="btn btn-danger" disabled={!record || isEditing} onClick={handleDeleteAction}>
              刪除
            </button>
          </div>
          <div className="action-bar-center">
            <button className="btn btn-sm" disabled={isEditing || currentIdx <= 0}
              onClick={() => navTo(0)}>|&lt;</button>
            <button className="btn btn-sm" disabled={isEditing || currentIdx <= 0}
              onClick={() => navTo(currentIdx - 1)}>&lt;</button>
            <span style={{ fontSize: 12, color: '#666', margin: '0 8px' }}>
              {allIds.length > 0 ? `${currentIdx + 1} / ${allIds.length}` : '0 / 0'}
            </span>
            <button className="btn btn-sm" disabled={isEditing || currentIdx >= allIds.length - 1}
              onClick={() => navTo(currentIdx + 1)}>&gt;</button>
            <button className="btn btn-sm" disabled={isEditing || currentIdx >= allIds.length - 1}
              onClick={() => navTo(allIds.length - 1)}>&gt;|</button>
          </div>
          <div className="action-bar-right">
            <button className="btn" onClick={handleExit}>
              {isEditing ? '取消' : '離開'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default M206Form;
